import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

/**
 * Shared date-range control for the active-users screens.
 * Manual From/To pickers plus one-click presets (Last 7 / Last 30 / This month).
 *
 * Controlled by the parent (which owns fetch state). This component only
 * computes preset windows and emits events.
 *
 * Props:
 *   preset        'last7' | 'last30' | 'thisMonth' | 'custom' | null
 *   displayFrom   Date | null   (for the From picker)
 *   displayTo     Date | null   (for the To picker)
 *   hasRange      boolean       (controls the Clear button)
 *   today         Date          (injectable for tests; defaults to now)
 *   onPreset(key, fromStr, toStr)   preset chip clicked
 *   onFromChange(date)              manual From edit
 *   onToChange(date)                manual To edit
 *   onSearch()                      Search clicked
 *   onClear()                       Clear clicked
 */
export const fmt = (date) =>
  date.getFullYear() +
  '-' +
  String(date.getMonth() + 1).padStart(2, '0') +
  '-' +
  String(date.getDate()).padStart(2, '0');

const addDays = (date, days) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + days);
  return d;
};

export const presetRange = (key, today) => {
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let start;
  if (key === 'last7') start = addDays(end, -6);
  else if (key === 'last30') start = addDays(end, -29);
  else if (key === 'thisMonth') start = new Date(end.getFullYear(), end.getMonth(), 1);
  else return { fromStr: '', toStr: '' };
  return { fromStr: fmt(start), toStr: fmt(end) };
};

const PRESETS = [
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'thisMonth', label: 'This month' },
];

const DateRangeBar = ({
  preset = null,
  displayFrom = null,
  displayTo = null,
  hasRange = false,
  today = new Date(),
  onPreset,
  onFromChange,
  onToChange,
  onSearch,
  onClear,
  leading = null,
  children = null,
}) => {
  const handlePreset = (key) => {
    const { fromStr, toStr } = presetRange(key, today);
    if (onPreset) onPreset(key, fromStr, toStr);
  };

  const presets = (
    <div className="au-presets" role="group" aria-label="Quick date ranges">
      {PRESETS.map((p) => (
        <button
          key={p.key}
          type="button"
          className={`au-preset${preset === p.key ? ' au-preset--active' : ''}`}
          aria-pressed={preset === p.key}
          onClick={() => handlePreset(p.key)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  const dateControls = (
    <>
      <DatePicker
        selected={displayFrom}
        onChange={onFromChange}
        dateFormat="dd/MM/yyyy"
        placeholderText="From Date"
        className="au-date-input"
      />
      <DatePicker
        selected={displayTo}
        onChange={onToChange}
        dateFormat="dd/MM/yyyy"
        placeholderText="To Date"
        className="au-date-input"
      />

      <button type="button" className="au-btn au-btn--primary" onClick={onSearch}>Search</button>
      {hasRange && (
        <button type="button" className="au-btn au-btn--ghost" onClick={onClear}>Clear</button>
      )}
    </>
  );

  return (
    <div className="au-daterange" data-testid="au-daterange">
      {/* Optional controls placed first in the panel (e.g. the page's search box and
          subscription select), so all filters share one frame. */}
      {leading && <div className="au-daterange__lead">{leading}</div>}
      {/* With leading controls the date controls are grouped, so they wrap onto the
          next line as one unit instead of splitting around the search box, and
          From/To + buttons stay together when the presets wrap. */}
      {leading ? (
        <div className="au-daterange__range">
          {presets}
          <div className="au-daterange__when">{dateControls}</div>
        </div>
      ) : (
        <>
          {presets}
          {dateControls}
        </>
      )}

      {/* Optional extra controls (e.g. an App Version dropdown), rendered inside
          the same panel. Nothing is rendered when the parent passes no children. */}
      {children && <div className="au-daterange__extra">{children}</div>}
    </div>
  );
};

export default DateRangeBar;
